/**
 * Next.js is configured with `trailingSlash: true`. Dynamic path builders that
 * feed <Link>, router.push, share URLs, and GTM must emit the final URL (with `/`)
 * so crawlers never discover the no-slash variant that only 308-redirects.
 *
 * Keep static route strings without a trailing slash: `router.pathname` never
 * includes one, and several call sites compare pathname === Routes.*.
 */
function withTrailingSlash(path: string): string {
  if (!path || path === '/') return '/';
  const hashIndex = path.indexOf('#');
  const hash = hashIndex >= 0 ? path.slice(hashIndex) : '';
  const beforeHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path;
  const queryIndex = beforeHash.indexOf('?');
  const query = queryIndex >= 0 ? beforeHash.slice(queryIndex) : '';
  const pathname =
    queryIndex >= 0 ? beforeHash.slice(0, queryIndex) : beforeHash;
  const normalized = pathname.endsWith('/') ? pathname : `${pathname}/`;
  return `${normalized}${query}${hash}`;
}

export const Routes = {
  addresses: '/addresses',
  home: '/',
  checkout: '/checkout',
  checkoutDigital: '/checkout/digital',
  checkoutGuest: '/checkout/guest',
  checkoutRecipe: '/checkout/recipe',
  profile: '/profile',
  verifyEmail: '/verify-email',
  changePassword: '/change-password',
  orders: '/orders',
  order: (tracking_number: string) =>
    withTrailingSlash(`/orders/${encodeURIComponent(tracking_number)}`),
  refunds: '/refunds',
  help: '/help',
  logout: '/logout',
  auth: '/auth',
  coupons: '/offers',
  orderReceived: '/order-received',
  products: '/products',
  product: (slug: string, productId: string) =>
    withTrailingSlash(
      `/${encodeURIComponent(slug)}/product/${encodeURIComponent(productId)}`,
    ),
  privacy: '/privacy',
  terms: '/terms',
  refundPolicies: '/refund-policies',
  customerRefundPolicies: '/customer-refund-policies',
  vendorRefundPolicies: '/vendor-refund-policies',
  contactUs: '/contact',
  shops: '/shops',
  cards: '/cards',
  shop: (slug: string) =>
    withTrailingSlash(`/shops/${encodeURIComponent(slug)}`),
  downloads: '/downloads',
  authors: '/authors',
  author: (slug: string) =>
    withTrailingSlash(`/authors/${encodeURIComponent(slug)}`),
  manufacturers: '/manufacturers',
  manufacturer: (slug: string) =>
    withTrailingSlash(`/manufacturers/${encodeURIComponent(slug)}`),
  nearByShop: ({ lat, lng }: { lat: string; lng: string }) =>
    withTrailingSlash(
      `/shops/search?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(
        lng,
      )}`,
    ),
  search: '/search',
  category: (slug: string) =>
    withTrailingSlash(`/categorias/${encodeURIComponent(slug)}`),
  wishlists: '/wishlists',
  questions: '/questions',
  reports: '/reports',
  flashSaleSingle: (slug: string) =>
    withTrailingSlash(`/flash-sales/${encodeURIComponent(slug)}`),
  flashSale: '/flash-sales',
  notifyLogs: '/notification',
  notifyLogsSingle: (id: string) =>
    withTrailingSlash(`/notification/${encodeURIComponent(id)}`),
  becomeSeller: '/become-seller',
  wallet: '/wallet',
  walletHistory: '/wallet/history',
  walletRecharge: '/wallet/recharge',
  notifications: '/notificacoes',
  quemSomos: '/quem-somos',
  acoesSociais: '/acoes-sociais',
  licenses: '/licencas',
  entregaRapidaSaoPaulo: '/entrega-rapida-sao-paulo',
  farmaciaOnlineConfiavel: '/farmacia-online-confiavel',
  bula: (id: string) =>
    `/bulas/${encodeURIComponent(String(id).toLowerCase())}.pdf`,
  downloadApp: '/baixe-o-app',
  politica: '/institucional#politica',
  termos: '/institucional#termos',
  parceirosDeEntrega: '/parceiros-de-entrega',
  friendsIndication: '/indique-amigos',
  compraComReceita: '/compra-com-receita',
};
